import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { EcommerceTermsProvider, useEcommerceTerms } from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";
const fallbackCollections = [{
  index: "§ A.01",
  title: "Apparel Transfers",
  descriptor: "High-stretch DTF prints designed for cotton and poly-blend garments.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/apparel_transfers.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/apparel_transfers.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/apparel_transfers.png-wvc-sizes"
}, {
  index: "§ A.02",
  title: "Sportswear Tech",
  descriptor: "Durable, breathable transfers engineered for high-performance athletic gear.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/sportswear_tech.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/sportswear_tech.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/sportswear_tech.png-wvc-sizes"
}, {
  index: "§ A.03",
  title: "Promotional Goods",
  descriptor: "Vibrant, crisp prints for bags, hats, and corporate accessories.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/promotional_goods.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/promotional_goods.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/promotional_goods.webp-wvc-sizes"
}, {
  index: "§ A.04",
  title: "Custom Patches",
  descriptor: "Detailed heat-press patches for a premium, textured finish.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/custom_patches.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/custom_patches.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/custom_patches.png-wvc-sizes"
}, {
  index: "§ A.05",
  title: "Eco-Friendly Series",
  descriptor: "Sustainable, water-based ink transfers for the conscious brand.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/eco_friendly_series.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/eco_friendly_series.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/eco_friendly_series.png-wvc-sizes"
}, {
  index: "§ A.06",
  title: "Bulk Production",
  descriptor: "High-volume transfer sheets optimized for rapid, industrial application.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/bulk_production.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/bulk_production.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/bulk_production.png-wvc-sizes"
}];
function CuratedCollectionsConsumer() {
  const {
    terms,
    loading,
    error
  } = useEcommerceTerms();
  if (loading) {
    return <div className="grid grid-cols-1 gap-px bg-border sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="bg-card p-7 min-h-[420px] animate-pulse">
            <div className="aspect-[4/5] w-full bg-muted" />
            <div className="mt-5 h-3 w-20 bg-muted" />
            <div className="mt-3 h-6 w-3/4 bg-muted" />
            <div className="mt-3 h-3 w-full bg-muted" />
          </div>)}
      </div>;
  }
  if (error || terms.length === 0) {
    return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        {fallbackCollections.map((collection, i) => <article data-index={i} key={collection.index} className="group relative border-t border-l border-border p-7 transition-colors duration-200 last:border-r sm:[&:nth-child(2n)]:border-r lg:[&:nth-child(2n)]:border-r-0 lg:[&:nth-child(3n)]:border-r [&:nth-last-child(-n+1)]:border-b sm:[&:nth-last-child(-n+2)]:border-b lg:[&:nth-last-child(-n+3)]:border-b">
            <CornerBrackets />
            <div className="relative aspect-[4/5] w-full overflow-hidden bg-muted">
              <img src={collection.image} data-wvc-srcset={collection.srcset} data-wvc-sizes={collection.sizes} alt={collection.title} loading="lazy" decoding="async" className="h-full w-full object-cover opacity-90 transition-transform duration-[400ms] ease-out group-hover:scale-[1.02]" />
              <div aria-hidden="true" className="pointer-events-none absolute inset-0 mix-blend-multiply" style={{
            backgroundColor: "hsl(220 20% 4% / 0.2)"
          }} />
            </div>
            <p className="mt-6 font-mono text-[0.75rem] uppercase tracking-[0.2em] text-secondary">
              {collection.index}
            </p>
            <h3 className="mt-3 font-default text-[1.5rem] font-semibold leading-[1.3] tracking-tight text-foreground">
              {collection.title}
            </h3>
            <p className="mt-3 font-default text-[1.125rem] leading-[1.6] text-secondary">
              {collection.descriptor}
            </p>
            <Link to="#" className="mt-6 inline-flex items-center gap-2 font-default text-[1rem] text-foreground transition-colors duration-200 hover:text-primary">
              <span className="border-b border-foreground/40 pb-0.5 transition-[border-width] group-hover:border-b-2">
                Explore category
              </span>
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </article>)}
      </div>;
  }
  const displayTerms = terms.slice(0, 6);
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
      {displayTerms.map((term, i) => <article data-index={i} key={term.id} className="group relative border-t border-l border-border p-7 transition-colors duration-200 last:border-r sm:[&:nth-child(2n)]:border-r lg:[&:nth-child(2n)]:border-r-0 lg:[&:nth-child(3n)]:border-r [&:nth-last-child(-n+1)]:border-b sm:[&:nth-last-child(-n+2)]:border-b lg:[&:nth-last-child(-n+3)]:border-b">
          <CornerBrackets />
          <div className="relative aspect-[4/5] w-full overflow-hidden bg-muted">
            {term.image_url ? <img src={term.image_url} alt={term.name} loading="lazy" decoding="async" className="h-full w-full object-cover opacity-90 transition-transform duration-[400ms] ease-out group-hover:scale-[1.02]" /> : <img src={fallbackCollections[i % fallbackCollections.length].image} data-wvc-srcset={fallbackCollections[i % fallbackCollections.length].srcset} data-wvc-sizes={fallbackCollections[i % fallbackCollections.length].sizes} alt={term.name} loading="lazy" decoding="async" className="h-full w-full object-cover opacity-90 transition-transform duration-[400ms] ease-out group-hover:scale-[1.02]" />}
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 mix-blend-multiply" style={{
          backgroundColor: "hsl(220 20% 4% / 0.2)"
        }} />
          </div>
          <p className="mt-6 font-mono text-[0.75rem] uppercase tracking-[0.2em] text-secondary">
            § A.{String(i + 1).padStart(2, "0")}
          </p>
          <h3 className="mt-3 font-default text-[1.5rem] font-semibold leading-[1.3] tracking-tight text-foreground">
            {term.name}
          </h3>
          <p className="mt-3 font-default text-[1.125rem] leading-[1.6] text-secondary">
            {term.description || "Professional grade transfer solutions for your unique project needs."}
          </p>
          <Link to={term.link || "/ShopPage"} className="mt-6 inline-flex items-center gap-2 font-default text-[1rem] text-foreground transition-colors duration-200 hover:text-primary">
            <span className="border-b border-foreground/40 pb-0.5 transition-[border-width] group-hover:border-b-2">
              Explore category
            </span>
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </article>)}
    </div>;
}
function CornerBrackets() {
  return <>
      <span aria-hidden="true" className="pointer-events-none absolute top-3 left-3 h-3.5 w-3.5 border-t border-l border-border transition-colors duration-[250ms] ease-out group-hover:border-primary" />
      <span aria-hidden="true" className="pointer-events-none absolute top-3 right-3 h-3.5 w-3.5 border-t border-r border-border transition-colors duration-[250ms] ease-out group-hover:border-primary" />
      <span aria-hidden="true" className="pointer-events-none absolute bottom-3 left-3 h-3.5 w-3.5 border-b border-l border-border transition-colors duration-[250ms] ease-out group-hover:border-primary" />
      <span aria-hidden="true" className="pointer-events-none absolute bottom-3 right-3 h-3.5 w-3.5 border-b border-r border-border transition-colors duration-[250ms] ease-out group-hover:border-primary" />
    </>;
}
const wp_term_query_params = {
  taxonomy: "product_cat",
  per_page: 6,
  orderby: "name",
  order: "asc"
};
const wp_term_query = new WP_Term_Query(wp_term_query_params);
export default function Categories() {
  return <section data-section-id="1920" id="browse-transfer-categories" className="relative bg-background text-foreground border-t border-border py-24 md:py-32 overflow-hidden">
      <div className="mx-auto max-w-[1280px] px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-10">
          <div className="relative lg:col-span-4">
            <span aria-hidden="true" className="pointer-events-none absolute -top-6 -left-2 select-none font-default font-bold text-foreground/[0.04] leading-none" style={{
            fontSize: "14rem"
          }}>
              III
            </span>

            <div className="relative lg:sticky lg:top-28">
              <p className="font-mono text-[0.75rem] font-medium uppercase tracking-[0.2em] text-primary">
                § 03 — TRANSFER SOLUTIONS
              </p>

              <h2 className="mt-6 font-default text-[clamp(2.5rem,8vw,5.5rem)] font-bold leading-[1.0] tracking-tighter text-foreground">
                Precision Prints.
              </h2>

              <div className="mt-7 flex items-center" aria-hidden="true">
                <span className="block h-px w-[160px] bg-border" />
                <span className="ml-0 -translate-x-px block h-1.5 w-1.5 bg-primary" />
              </div>

              <p className="mt-6 max-w-md font-default text-[1.125rem] leading-[1.6] text-secondary">
                From high-stretch apparel transfers to durable industrial patches, 
                we provide the professional tools to bring your digital designs to life.
              </p>

              <div className="mt-8">
                <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-4 h-auto font-bold text-[1rem] tracking-tight">
                  <Link to="#">Initialize Order</Link>
                </Button>
              </div>

              <p className="mt-10 hidden font-default text-[0.875rem] leading-[1.4] text-secondary/60 lg:block">
                Technical specs: CMYK+W / 1440 DPI / Fast-Dry
              </p>
            </div>
          </div>

          <div className="lg:col-span-8">
            <EcommerceTermsProvider className="curated-collections-grid-wrapper" wp_term_query={wp_term_query}>
              <CuratedCollectionsConsumer />
            </EcommerceTermsProvider>
          </div>
        </div>
      </div>
    </section>;
}